import React, { useEffect, useState } from 'react';
import { MainLayout } from '../../Components/Layouts/MainLayout/MainLayout';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Column from './Components/Column/Column';
import {
  FormControl,
  Autocomplete,
  TextField,
} from '@mui/material';

import { PlannerContainer, SelectContainer } from './styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getJobs } from '../../Services/Jobs';
import { useTranslation } from 'react-i18next';
import { getCandidateJobs, updateCandidateJobStatus } from '../../Services/CandidateJobs';
import usePlannerCandidateJobs from '../../Hooks/usePlannerCandidateJobs';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../Stores/useAuthStore';
import { ConfirmActionDialog } from '../../Components/Common/ConfirmActionDialog/ConfirmActionDialog';
import { Loader } from '../../Components/Loader/Loader';
import { useJobStore } from '../../Stores/useGeneralAppStore';

type Columns = Record<string, CandidateJob[]>;

export const Planner: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogActions, setConfirmDialogActions] = useState<{
    confirm: () => void;
    cancel: () => void;
  } | null>(null);
  const { selectedStoreJobId } = useJobStore();
  const [columns, setColumns] = useState<Columns>({
    'new-candidate': [],
    'in-progress-internal-client': [],
    'in-progress-external-client': [],
    'pre-selected': [],
    'not-selected-internal-client': [],
    'not-selected-external-client': [],
    hired: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  const shouldFilterByUserId = (): boolean | undefined => {
    return user?.isInternal &&
      !user?.isAdmin &&
      !user?.isRecruiter;
  };

  const newCandidateLabel = t('planner.columns.new_candidate');
  const inProgressInternalClientLabel = t('planner.columns.in_progress_client.internal');
  const inProgressExternalClientLabel = t('planner.columns.in_progress_client.external');
  const preSelectedLabel = t('planner.columns.pre_selected');
  const hiredLabel = t('planner.columns.hired');
  const notSelectedInternalClientLabel = t('planner.columns.not_selected.internal_client');
  const notSelectedExternalClientLabel = t('planner.columns.not_selected.external_client');
  const selectJobLabel = t('planner.select_job');
  const updateStatusSuccessMessage = t('jobs.status.update.success_message');
  const confirmMessage = t('planner.change_status.confirm_message');

  const { data: jobs = [] } = useQuery({
    queryKey: ['getJobs'],
    queryFn: async () => {
      setIsLoading(true);
      const response = await getJobs({
        page: 1,
        count: 200,
        userId: shouldFilterByUserId() ? `${user?.id}` : '',
        status: 'Active',
      });
      setIsLoading(false);
      return response.items;
    },
  });

  const { data: candidateJobs } = useQuery({
    queryKey: ['getCandidateJobs', selectedJob?.id],
    queryFn: async () => {
      setIsLoading(true);
      const response = await getCandidateJobs({
        page: 1,
        count: 200,
        jobId: selectedJob?.id?.toString() || '',
      });
      setIsLoading(false);
      return response.items;
    },
    enabled: !!selectedJob?.id,
  });

  const {
    inProgressTalentAcquisitionCandidates,
    inProgressInternalClientCandidates,
    inProgressExternalClientCandidates,
    preSelectedCandidates,
    notSelectedInternalClientCandidates,
    notSelectedExternalClientCandidates,
    hiredCandidates,
  } = usePlannerCandidateJobs(candidateJobs && candidateJobs.length > 0 ? candidateJobs : []);

  useEffect(() => {
    if (jobs && jobs.length > 0) {
      // First check if there's a selected job ID in the store
      const job = jobs.find((job) => job.id === selectedStoreJobId);
      if (selectedStoreJobId && job) {
        setSelectedJob(job);
      } else {
        // Fall back to the first job if no valid stored ID
        setSelectedJob(jobs[0]);
      }
    }
  }, [jobs, selectedStoreJobId]);

  useEffect(() => {
    setColumns({
      'new-candidate': inProgressTalentAcquisitionCandidates,
      'in-progress-internal-client': inProgressInternalClientCandidates,
      'in-progress-external-client': inProgressExternalClientCandidates,
      'pre-selected': preSelectedCandidates,
      'not-selected-internal-client': notSelectedInternalClientCandidates,
      'not-selected-external-client': notSelectedExternalClientCandidates,
      hired: hiredCandidates,
    });
  }, [
    inProgressTalentAcquisitionCandidates,
    inProgressInternalClientCandidates,
    inProgressExternalClientCandidates,
    preSelectedCandidates,
    notSelectedInternalClientCandidates,
    notSelectedExternalClientCandidates,
    hiredCandidates,
  ]);

  const { mutate: updateCandidateJobStatusMutation } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status: string; subStatus: string } }) =>
      updateCandidateJobStatus(id, data),
    onError: (error: { response: { data: string } }) => {
      toast.error(error?.response?.data);
    },
    onSuccess: () => {
      toast.success(updateStatusSuccessMessage);
      queryClient.invalidateQueries({ queryKey: ['getCandidateJobs', selectedJob?.id] });
    },
  });

  const handleDropAction = (itemId: string, newColumnId: string) => {
    let status = '';
    let subStatus = '';

    switch (newColumnId) {
      case 'new-candidate':
        status = 'InProgressTalentAcquisition';
        subStatus = 'TalentAcquisitionInterview';
        break;
      case 'in-progress-internal-client':
        status = 'InProgressClient';
        subStatus = 'Internal';
        break;
      case 'in-progress-external-client':
        status = 'InProgressClient';
        subStatus = 'External';
        break;
      case 'pre-selected':
        status = 'InProgressClient';
        subStatus = 'PreSelected';
        break;
      case 'not-selected-internal-client':
        status = 'NotSelected';
        subStatus = 'ByInternalClient';
        break;
      case 'not-selected-external-client':
        status = 'NotSelected';
        subStatus = 'ByExternalClient';
        break;
      case 'hired':
        status = 'hired';
        break;
      default:
        status = 'unknown';
        subStatus = 'unknown';
        break;
    }
    updateCandidateJobStatusMutation({ id: itemId, data: { status, subStatus } });
  };

  const handleDropConfirm = async (itemId: string, newColumnId: string) => {
    if (!user?.isAdmin && !user?.isRecruiter) {
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      setConfirmDialogActions({
        confirm: () => {
          setShowConfirmDialog(false);
          resolve(true);
        },
        cancel: () => {
          setShowConfirmDialog(false);
          resolve(false);
        },
      });
      setShowConfirmDialog(true);
    });

    if (confirmed) {
      handleDropAction(itemId, newColumnId);
    }
  };

  return (
    <MainLayout>
      <DndProvider backend={HTML5Backend}>
        <SelectContainer>
          <FormControl fullWidth>
            <Autocomplete
              sx={{ marginTop: 1 }}
              id="job-autocomplete"
              options={Array.isArray(jobs) ? jobs : []}
              getOptionLabel={(option) => `${option.jobReference} - ${option.position}`}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              onChange={(_, newValue) => {
                setSelectedJob(newValue);
              }}
              value={selectedJob}
              renderInput={(params) => <TextField {...params} label={selectJobLabel} />}
              fullWidth
            />
          </FormControl>
        </SelectContainer>
        <PlannerContainer>
          <Column
            title={newCandidateLabel}
            items={columns['new-candidate']}
            onDropItem={handleDropConfirm}
            columnId="new-candidate"
          />
          <Column
            title={inProgressInternalClientLabel}
            items={columns['in-progress-internal-client']}
            onDropItem={handleDropConfirm}
            columnId="in-progress-internal-client"
          />
          <Column
            title={inProgressExternalClientLabel}
            items={columns['in-progress-external-client']}
            onDropItem={handleDropConfirm}
            columnId="in-progress-external-client"
          />
          <Column
            title={preSelectedLabel}
            items={columns['pre-selected']}
            onDropItem={handleDropConfirm}
            columnId="pre-selected"
          />
          <Column
            title={notSelectedInternalClientLabel}
            items={columns['not-selected-internal-client']}
            onDropItem={handleDropConfirm}
            columnId="not-selected-internal-client"
          />
          <Column
            title={notSelectedExternalClientLabel}
            items={columns['not-selected-external-client']}
            onDropItem={handleDropConfirm}
            columnId="not-selected-external-client"
          />
          <Column
            title={hiredLabel}
            items={columns['hired']}
            onDropItem={handleDropConfirm}
            columnId="hired"
          />
        </PlannerContainer>
      </DndProvider>
      <ConfirmActionDialog
        open={showConfirmDialog}
        handleClose={() => confirmDialogActions?.cancel()}
        handleConfirm={() => confirmDialogActions?.confirm()}
        description={confirmMessage}
      />
      <Loader isLoading={isLoading} />
    </MainLayout>
  );
};
